/**
 * Achsen und Zahlenformate der Ticket-Blöcke in den Bereichs-KPIs.
 *
 * **Warum die Datei nicht mehr `ticketBloecke.ts` heißt.** Daneben liegt die
 * Komponente `TicketBloecke.tsx`, und auf einem case-insensitiven
 * Dateisystem — also auf jedem Windows-Arbeitsplatz — sind das dieselben zwei
 * Namen. TypeScript brach mit `TS1149` ab, `pnpm build` und `pnpm typecheck`
 * waren für `apps/web` unbenutzbar, und Vite löste `./TicketBloecke.js` auf
 * diese Datei auf: Der Dev-Server startete, die Seite blieb leer, und in der
 * Konsole stand nur, `ReaktionBlock` werde nicht exportiert. In der CI unter
 * Linux fiel nichts davon auf (eingeführt mit #329, behoben 2026-11-05).
 *
 * **Regel:** Zwei Dateien im selben Ordner dürfen sich nicht allein in der
 * Groß- und Kleinschreibung unterscheiden. Ein Name, der nur auf der Hälfte
 * der Arbeitsplätze funktioniert, ist keiner.
 */
/** Monatsachse wie die Geldkurven der Bereichs-KPIs (`routes/kpi/bereich.tsx`). */
export const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/**
 * Breite der rechten Achse aller Diagramme auf der Monatsachse: Die Geldkurven
 * tragen dort eine unsichtbare Platzhalterachse, die Ticket-Diagramme ihre
 * echte — so enden alle Monate untereinander an derselben Stelle.
 */
export const RECHTE_ACHSE_BREITE = 60;

/** `YYYY-MM` → erster Tag und erster Tag des Folgemonats, ohne Umweg über Date. */
export function monatsGrenzen(monat: string): { von: string; bis: string } {
  const j = Number(monat.slice(0, 4));
  const m = Number(monat.slice(5, 7));
  const folge = m === 12 ? `${j + 1}-01` : `${j}-${String(m + 1).padStart(2, "0")}`;
  return { von: `${monat}-01`, bis: `${folge}-01` };
}

export function prozent(v: number | null): string {
  return v == null ? "–" : `${v.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/** Reaktionszeit in Geschäftszeit, immer in Stunden mit einer Nachkommastelle. */
export function stunden(ms: number | null): string {
  return ms == null ? "–" : `${(ms / 3600_000).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

/** Liegezeit in Arbeitstagen, eine Nachkommastelle — nie mit Kalendertagen mischen. Die Einheit kommt aus der Übersetzung (de „AT“, en „WD“). */
export function arbeitstageText(tage: number | null, einheit = "AT"): string {
  return tage == null ? "–" : `${tage.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${einheit}`;
}

/** Veränderung gegen das Vorjahr in Prozent, eine Nachkommastelle, mit Vorzeichen; bei Vorjahr 0 ohne Aussage. */
export function veraenderung(aktuell: number, vorjahr: number): string {
  if (vorjahr === 0) return "–";
  // Erst runden, dann das Vorzeichen: sonst stünde bei -0,04 % ein „-0,0 %".
  const p = Math.round(((aktuell - vorjahr) / vorjahr) * 1000) / 10;
  const zahl = Math.abs(p).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${p > 0 ? "+" : p < 0 ? "-" : ""}${zahl} %`;
}

export interface MonateSumme {
  erstellt: number;
  geloest: number;
  vorjahrErstellt: number;
  vorjahrGeloest: number;
}

/**
 * Die Summenzeile über dem Monatsdiagramm: Spanne vom ersten bis zum letzten
 * Monat mit Wert, Summen und Veränderung gegen das Vorjahr als fertiger Text.
 * `null`, wenn noch kein Monat einen Wert hat.
 */
export function summenZeile(
  monate: Array<{ monat: string; offen: number | null }>,
  summe: MonateSumme,
): { von: string; bis: string; jahr: string; erstellt: string; vjErstellt: string; deltaErstellt: string; geloest: string; vjGeloest: string; deltaGeloest: string } | null {
  const mitWert = monate.filter((m) => m.offen !== null);
  if (mitWert.length === 0) return null;
  const name = (monat: string) => MONAT_KURZ[Number(monat.slice(5, 7)) - 1]!;
  const zahl = (n: number) => n.toLocaleString("de-DE");
  return {
    von: name(mitWert[0]!.monat),
    bis: name(mitWert[mitWert.length - 1]!.monat),
    jahr: mitWert[0]!.monat.slice(0, 4),
    erstellt: zahl(summe.erstellt),
    vjErstellt: zahl(summe.vorjahrErstellt),
    deltaErstellt: veraenderung(summe.erstellt, summe.vorjahrErstellt),
    geloest: zahl(summe.geloest),
    vjGeloest: zahl(summe.vorjahrGeloest),
    deltaGeloest: veraenderung(summe.geloest, summe.vorjahrGeloest),
  };
}
